import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { SpawnedCoin, WordGate } from "../types";
import type { WordDTO } from "@shared/types";
import { LANE_POSITIONS } from "@shared/constants";
import { createRng } from "@shared/game";
import { createObstacle, type ObstacleController, type ObstacleKind } from "./obstacles";
import type { Path } from "./Path";
import { hitParadeCannonAnchors } from "./hitParadeDecor";

export interface SpawnPlan {
  obstacles: ObstacleController[];
  coins: SpawnedCoin[];
  gates: WordGate[];
}

const OBSTACLE_MIX: { kind: ObstacleKind; weight: number }[] = [
  { kind: "barrier", weight: 3 },
  { kind: "crate", weight: 3 },
  { kind: "boulder", weight: 2 },
  { kind: "log", weight: 2 },
  { kind: "sweeper", weight: 2 },
  { kind: "swing-log", weight: 1 },
  { kind: "lava", weight: 1 },
  { kind: "spike-roller", weight: 1 },
];

function pickKind(rng: () => number): ObstacleKind {
  const total = OBSTACLE_MIX.reduce((a, b) => a + b.weight, 0);
  let r = rng() * total;
  for (const o of OBSTACLE_MIX) {
    if ((r -= o.weight) <= 0) return o.kind;
  }
  return "barrier";
}

export function buildSpawnPlan(opts: {
  seed: number;
  courseLength: number;
  objectsCount: number;
  coinsCount: number;
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  mode?: "default" | "hit-parade" | "ice-crack" | "door-dash" | "rolling-tubes" | "circle-spinner" | "maze-rush" | "space-ship" | "supermarket-sweep" | "block-climb" | "river-boat" | "double-spinner" | "train-roof";
  /** Ice-crack only: pre-computed gate + spinner arc-length positions from
   *  iceCrackDecor (which owns the layout, since spinners sit on platforms). */
  iceCrackPlan?: { gatePositions: number[]; spinnerPositions: number[] };
  /** Door-dash only: same idea — Path owns the s-positions, we just wrap them
   *  as gate/obstacle records. */
  doorDashPlan?: { gatePositions: number[]; spinnerPositions: number[] };
  /** Rolling-tubes only: 5 gate positions (one per interior platform). No
   *  obstacles needed beyond the tubes themselves. */
  rollingTubesPlan?: { gatePositions: number[] };
  /** Circle-spinner only: 4 gate positions between the 5 circular platforms.
   *  Spinners themselves live in the decor module. */
  circleSpinnerPlan?: { gatePositions: number[] };
  /** Maze-rush: 2 gate positions (mid platform + finish platform). */
  mazeRushPlan?: { gatePositions: number[] };
  /** Space-ship: gate positions per gated island. */
  spaceShipPlan?: { gatePositions: number[] };
  /** Supermarket-sweep: 3 gate positions along the aisle. */
  supermarketSweepPlan?: { gatePositions: number[] };
  /** Block-climb: 3 gate positions (2 safe-pad gates + finish gate). */
  blockClimbPlan?: { gatePositions: number[] };
  /** River-boat: gate per safe dock (3 total — 2 mid + finish). */
  riverBoatPlan?: { gatePositions: number[] };
  /** Train-roof: gate per gated boxcar (5 total). */
  trainRoofPlan?: { gatePositions: number[] };
}): SpawnPlan {
  if (opts.mode === "hit-parade") return buildHitParadePlan(opts);
  if (opts.mode === "ice-crack") return buildIceCrackPlan(opts);
  if (opts.mode === "door-dash") return buildDoorDashPlan(opts);
  if (opts.mode === "rolling-tubes") return buildRollingTubesPlan(opts);
  if (opts.mode === "circle-spinner") return buildCircleSpinnerPlan(opts);
  if (opts.mode === "double-spinner") {
    // Survival mode has no gates / questions. Return an empty plan so the
    // existing engine code paths don't try to render question UI.
    return { gates: [], obstacles: [], coins: [] };
  }
  if (opts.mode === "maze-rush") return buildMazeRushPlan(opts);
  if (opts.mode === "space-ship") return buildSpaceShipPlan(opts);
  if (opts.mode === "supermarket-sweep") return buildSupermarketSweepPlan(opts);
  if (opts.mode === "block-climb") return buildBlockClimbPlan(opts);
  if (opts.mode === "river-boat") return buildRiverBoatPlan(opts);
  if (opts.mode === "train-roof") return buildTrainRoofPlan(opts);
  const rng = createRng(opts.seed);
  const startPad = Math.min(48, Math.max(32, opts.courseLength * 0.12));
  const endPad = Math.min(220, Math.max(56, opts.courseLength * 0.22));
  const usable = Math.max(90, opts.courseLength - startPad - endPad);

  const gateWords = opts.words.slice(0, opts.questionsCount);
  const gateStep = usable / (gateWords.length + 1);
  const gates: WordGate[] = gateWords.map((word, i) => ({
    id: i,
    z: placeAwayFromBridge(startPad + gateStep * (i + 1), opts.path, opts.courseLength),
    word,
    triggered: false,
  }));

  const obstacles: ObstacleController[] = [];
  let oid = 0;
  const sweeperZ = startPad + usable * 0.42;
  obstacles.push(createObstacle("sweeper", { id: oid++, z: sweeperZ, lane: 2, path: opts.path }));
  const bridge = opts.path.narrowBridgeSections()[0];
  if (bridge) {
    obstacles.push(createObstacle("swing-log", { id: oid++, z: bridge.center, lane: 2, path: opts.path }));
  }
  const cannonSlots = [startPad + usable * 0.28, startPad + usable * 0.66];
  for (let i = 0; i < cannonSlots.length; i++) {
    let s = cannonSlots[i]!;
    for (let n = 0; n < 6; n++) {
      const tooClose = gates.some((g) => Math.abs(g.z - s) < 15)
        || obstacles.some((o) => Math.abs(o.getZ() - s) < 12)
        || opts.path.isNearCorner(s, 12);
      if (!tooClose) break;
      s += 8;
    }
    if (s < startPad + 8 || s > opts.courseLength - endPad - 8) continue;
    obstacles.push(createObstacle("boulder", { id: oid++, z: s, lane: i % 2 === 0 ? 1 : 3, path: opts.path }));
  }
  let attempts = 0;
  // Multi-lane obstacles (lava, log, spike-roller) need a much wider corner
  // buffer because their lateral extent reaches almost path-edge — on a tight
  // bend the outside end can clip past the road tile.
  const cornerBuffer = (k: ObstacleKind) =>
    k === "lava" || k === "log" || k === "spike-roller" || k === "sweeper" || k === "swing-log" ? 16 : 9;
  while (obstacles.length < opts.objectsCount && attempts < opts.objectsCount * 8) {
    attempts++;
    const s = startPad + rng() * usable;
    if (gates.some((g) => Math.abs(g.z - s) < 14)) continue;
    if (obstacles.some((o) => Math.abs(o.getZ() - s) < 9)) continue;
    if (opts.path.narrowBridgeHalfWidthAt(s) != null) continue;
    const kind = pickKind(rng);
    if (opts.path.isNearCorner(s, cornerBuffer(kind))) continue;
    const lane = Math.floor(rng() * LANE_POSITIONS.length);
    obstacles.push(createObstacle(kind, { id: oid++, z: s, lane, path: opts.path }));
  }

  // Coins removed game-wide: the UI is intentionally minimal now.
  const coins: SpawnedCoin[] = [];

  return { obstacles, coins, gates };
}

function buildHitParadePlan(opts: {
  seed: number;
  courseLength: number;
  coinsCount: number;
  words: WordDTO[];
  questionsCount: number;
  path: Path;
}): SpawnPlan {
  const rng = createRng(opts.seed ^ 0x4a17b);
  const length = opts.courseLength;
  // Hit Parade: flat start ~30m, ramp from ~30→length-25, flat top to end.
  const flatStart = 30;
  const rampEnd = length - 25;
  const usable = Math.max(40, rampEnd - flatStart);

  // Word gates: spread along the climb. Pull them inward from the start/end of
  // the ramp so there's breathing room before the first question and after the
  // last one before the finish line.
  const gateWords = opts.words.slice(0, opts.questionsCount);
  const gateRegionStart = flatStart + Math.min(28, usable * 0.18);
  const gateRegionEnd = rampEnd - Math.min(28, usable * 0.18);
  const gateSpan = Math.max(0, gateRegionEnd - gateRegionStart);
  const gateStep = gateWords.length > 1 ? gateSpan / (gateWords.length - 1) : 0;
  const gates: WordGate[] = gateWords.map((word, i) => ({
    id: i,
    z: gateWords.length === 1 ? gateRegionStart + gateSpan / 2 : gateRegionStart + gateStep * i,
    word,
    triggered: false,
  }));

  const obstacles: ObstacleController[] = [];
  let oid = 0;

  // Bouncing balls: all spawned at the cannon positions. Each cannon fires
  // multiple balls (different lanes) so the cadence + sweep produces a
  // varied stream. The cannon visuals themselves are added by
  // buildHitParadeCannons in Game.ts using the same anchor list.
  const anchors = hitParadeCannonAnchors(opts.courseLength);
  const BALLS_PER_ANCHOR_LANE = [1, 3, 0, 4, 2]; // 5 balls per anchor, lane-staggered
  for (const anchor of anchors) {
    for (const lane of BALLS_PER_ANCHOR_LANE) {
      obstacles.push(createObstacle("bouncing-ball", {
        id: oid++, z: anchor.s, lane, path: opts.path,
      }));
    }
  }

  // Spinning bars — reduced count (was ~10 per run) and evenly spaced
  // across the full climb so the kid gets a single bar to time between
  // each pair of question gates instead of being swamped.
  const SPINNER_COUNT = 5; // half of the previous count
  const spinnerSlots: number[] = [];
  const spinnerStart = flatStart + 14;
  const spinnerEnd = rampEnd - 14;
  for (let i = 0; i < SPINNER_COUNT; i++) {
    const t = (i + 1) / (SPINNER_COUNT + 1);
    spinnerSlots.push(spinnerStart + (spinnerEnd - spinnerStart) * t);
  }
  // One long spinner per slot — arm now stretches across the full track
  // width so the kid has to time the dodge instead of side-stepping it.
  // tooClose() check kept against gates only (cannon-anchor balls live
  // at the top + middle anchors so spinner slots between gates don't
  // clash with them in practice).
  for (const z of spinnerSlots) {
    if (z < flatStart + 6 || z > rampEnd - 6) continue;
    // Only filter against gates here; ball spawns now live at the cannon
    // anchors and are mostly outside the gate region, so excluding them
    // was eating every spinner slot.
    if (gates.some((g) => Math.abs(g.z - z) < 10)) continue;
    obstacles.push(createObstacle("spinner", {
      id: oid++, z, lane: 2, path: opts.path,
      long: true,
    }));
  }

  // Coins removed for the minimal UI pass.
  const coins: SpawnedCoin[] = [];

  return { obstacles, coins, gates };
}

function buildIceCrackPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  iceCrackPlan?: { gatePositions: number[]; spinnerPositions: number[] };
}): SpawnPlan {
  const layout = opts.iceCrackPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, opts.questionsCount);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  const obstacles: ObstacleController[] = [];
  let oid = 0;
  for (const z of layout.spinnerPositions) {
    obstacles.push(createObstacle("ice-spinner", { id: oid++, z, lane: 2, path: opts.path }));
  }
  // Snowballs — flying past the player like Level 1's bouncing balls, but
  // tinted white. Two per spinner position spaced laterally so the player
  // has to weave between them while crossing the ice.
  const laneCycle = [0, 2, 4, 1, 3];
  layout.spinnerPositions.forEach((z, i) => {
    const a = laneCycle[i % laneCycle.length]!;
    const b = (a + 2) % LANE_POSITIONS.length;
    // Spawn ahead of the spinner so balls cascade toward the player while
    // they're still in the ice field, not on the platform.
    obstacles.push(createObstacle("snowball", { id: oid++, z: z + 6, lane: a, path: opts.path }));
    obstacles.push(createObstacle("snowball", { id: oid++, z: z + 12, lane: b, path: opts.path }));
  });
  return { obstacles, coins: [], gates };
}

function buildRiverBoatPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  riverBoatPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.riverBoatPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildBlockClimbPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  blockClimbPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.blockClimbPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildTrainRoofPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  trainRoofPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.trainRoofPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildSupermarketSweepPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  supermarketSweepPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.supermarketSweepPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildSpaceShipPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  spaceShipPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.spaceShipPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildMazeRushPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  mazeRushPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.mazeRushPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildCircleSpinnerPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  circleSpinnerPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.circleSpinnerPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  // 4 gates between 5 circles; if seed has fewer words, only place that many.
  const gateWords = opts.words.slice(0, layout.gatePositions.length);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildRollingTubesPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  rollingTubesPlan?: { gatePositions: number[] };
}): SpawnPlan {
  const layout = opts.rollingTubesPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  const gateWords = opts.words.slice(0, 5);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  return { obstacles: [], coins: [], gates };
}

function buildDoorDashPlan(opts: {
  words: WordDTO[];
  questionsCount: number;
  path: Path;
  doorDashPlan?: { gatePositions: number[]; spinnerPositions: number[] };
}): SpawnPlan {
  const layout = opts.doorDashPlan;
  if (!layout) return { obstacles: [], coins: [], gates: [] };
  // Always 5 gates for door-dash, even if questionsCount asks for fewer/more.
  const gateWords = opts.words.slice(0, 5);
  const gates: WordGate[] = layout.gatePositions.slice(0, gateWords.length).map((z, i) => ({
    id: i,
    z,
    word: gateWords[i]!,
    triggered: false,
  }));
  const obstacles: ObstacleController[] = [];
  let oid = 0;
  for (const z of layout.spinnerPositions) {
    // Default short-arm spinner (~3.8m sweep). The previous `long: true`
    // (~11m sweep) was wider than the narrowest door rows (1 cell ≈
    // 1.7m), so on certain phases the arm covered the ONLY open door
    // and there was no way through — unwinnable RNG. Short arm always
    // leaves a side path open.
    obstacles.push(createObstacle("spinner", {
      id: oid++, z, lane: 2, path: opts.path,
    }));
  }
  return { obstacles, coins: [], gates };
}

function placeAwayFromBridge(s: number, path: Path, courseLength: number) {
  const bridge = path.narrowBridgeSections()[0];
  if (!bridge || s < bridge.start - 14 || s > bridge.end + 14) return s;
  const before = bridge.start - 18;
  const after = bridge.end + 18;
  return THREE.MathUtils.clamp(
    Math.abs(s - before) < Math.abs(s - after) ? before : after,
    38,
    courseLength - 38,
  );
}

export function buildCoinMesh(c: SpawnedCoin, path?: Path): THREE.Object3D {
  const m = new THREE.Mesh(
    new THREE.CylinderGeometry(0.3, 0.3, 0.08, 16),
    new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.5, roughness: 0.3 }),
  );
  // Coin disc faces the player (perpendicular to path tangent).
  if (path) {
    const pos = path.positionAt(c.z, LANE_POSITIONS[c.lane]!);
    m.position.copy(pos);
    m.position.y = 1.0;
    m.rotation.y = path.yawAt(c.z);
    m.rotation.z = Math.PI / 2;
  } else {
    m.rotation.z = Math.PI / 2;
    m.position.set(LANE_POSITIONS[c.lane]!, 1.0, c.z);
  }
  m.castShadow = true;
  return m;
}

export function buildGateMesh(g: WordGate, path?: Path): THREE.Object3D {
  const grp = new THREE.Group();
  if (path) {
    const pos = path.positionAt(g.z, 0);
    grp.position.copy(pos);
    grp.rotation.y = path.yawAt(g.z);
  } else {
    grp.position.set(0, 0, g.z);
  }
  const beamMat = new THREE.MeshStandardMaterial({
    color: 0x9b5cf6,
    emissive: 0x6f3bd8,
    emissiveIntensity: 0.5,
  });
  const arch = new THREE.Mesh(new SoftBox(13, 0.4, 0.4), beamMat);
  arch.position.y = 4.2;
  grp.add(arch);
  for (const x of [-6.4, 6.4]) {
    const post = new THREE.Mesh(new SoftBox(0.4, 4.2, 0.4), beamMat);
    post.position.set(x, 2.1, 0);
    grp.add(post);
  }
  const ribbon = new THREE.Mesh(
    new SoftBox(12.2, 0.8, 0.05),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85 }),
  );
  ribbon.position.y = 4.2;
  ribbon.position.z = 0.25;
  grp.add(ribbon);
  return grp;
}
